import { useCallback, useEffect, useMemo, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { TimeSlotType } from 'shared-types/SpaPackage';

import { SpaPackageFormValues } from '../../SpaPackageForm.schema';
import { mapTimeslotsToDayAvailability } from '../SpaDayBooking.helpers';

type UseDateManagementParams = {
  form: UseFormReturn<SpaPackageFormValues>;
  selectedDateString: string;
  weekStartDate: string;
  setWeekStartDate: (date: string) => void;
  timeslots: Record<string, TimeSlotType[]> | undefined;
};

export function useDateManagement({
  form,
  selectedDateString,
  weekStartDate,
  setWeekStartDate,
  timeslots,
}: UseDateManagementParams) {
  const { setValue, getValues } = form;
  const previousDateRef = useRef(selectedDateString);

  useEffect(() => {
    if (!selectedDateString) return;

    const dateChanged = selectedDateString !== previousDateRef.current;

    if (dateChanged) {
      previousDateRef.current = selectedDateString;
      const currentGuests = getValues('guests') || [];
      const clearedGuests = currentGuests.map((guest) => ({
        ...guest,
        configurationGroups: undefined,
      }));
      setValue('guests', clearedGuests as SpaPackageFormValues['guests'], {
        shouldValidate: true,
      });
    }

    const currentArrivalTime = getValues('arrivalTime');
    if (!currentArrivalTime || dateChanged) {
      const availableTimeslots = timeslots?.[selectedDateString];
      if (availableTimeslots && availableTimeslots.length > 0) {
        setValue('arrivalTime', availableTimeslots[0], { shouldValidate: true });
      }
    }
  }, [setValue, getValues, selectedDateString, timeslots]);

  const availability = useMemo(
    () => mapTimeslotsToDayAvailability(timeslots, weekStartDate),
    [timeslots, weekStartDate]
  );

  const handleDateSelect = useCallback(
    (date: string) => {
      setValue('date', date, { shouldValidate: true });
      setValue('arrivalTime', undefined as SpaPackageFormValues['arrivalTime']);
    },
    [setValue]
  );

  const handleWeekNavigate = useCallback(
    (date: string) => {
      setWeekStartDate(date);
      setValue('date', date, { shouldValidate: true });
      setValue('arrivalTime', undefined as SpaPackageFormValues['arrivalTime']);
    },
    [setValue, setWeekStartDate]
  );

  return {
    availability,
    handleDateSelect,
    handleWeekNavigate,
  };
}
